import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, Star, Shield, AlertTriangle, Users, 
  TrendingUp, TrendingDown, Minus, Youtube, ExternalLink,
  CheckCircle, XCircle, Award, Utensils, Camera, Car,
  Clock, Sparkles, Info, Play, Plane, Train,
  Hotel, Download, Share2, GitCompare, Globe, FileText,
  HelpCircle, MessageSquare, AlertCircle, IndianRupee, Heart, ThumbsDown,
  ChevronDown, ChevronUp, ClipboardCheck, PenSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import ScoreCircle from '../components/ScoreCircle';
import CategoryRatingBar from '../components/CategoryRatingBar';
import { HelpfulFeedback } from '../components/FeedbackWidgets';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VenueReportPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, isLoggedIn, setShowLoginModal, requireLogin } = useAuth();
  const [report, setReport] = useState(null);
  const [isShortlisted, setIsShortlisted] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [showScoreExplanation, setShowScoreExplanation] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    if (location.state?.report) {
      setReport(location.state.report);
    } else {
      navigate('/');
    }
  }, [location, navigate]);

  const handleShortlist = async () => {
    if (!isLoggedIn) {
      requireLogin('shortlist a venue', () => handleShortlist());
      return;
    }
    
    try {
      await axios.post(`${API_URL}/shortlist/add?venue_name=${encodeURIComponent(report.venue_name)}&venue_location=${encodeURIComponent(report.location || '')}&token=${token}`);
      setIsShortlisted(true);
      toast.success('Venue added to shortlist!');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('Venue already shortlisted');
        setIsShortlisted(true);
      } else {
        toast.error('Failed to shortlist venue');
      }
    }
  };

  const handleReject = async () => {
    if (!isLoggedIn) {
      requireLogin('reject a venue', () => handleReject());
      return;
    }
    
    try {
      await axios.post(`${API_URL}/venues/reject?venue_name=${encodeURIComponent(report.venue_name)}&token=${token}`);
      setIsRejected(true);
      toast.success('Venue marked as rejected');
    } catch (error) {
      toast.error('Failed to reject venue');
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Generating PDF...');
      
      // Helper function to sanitize text for PDF
      const sanitizeText = (text) => {
        if (!text) return '';
        return text
          .replace(/[\u2018\u2019]/g, "'")
          .replace(/[\u201C\u201D]/g, '"')
          .replace(/\u2013/g, '-')
          .replace(/\u2014/g, '--')
          .replace(/\u2026/g, '...');
      };
      
      // Create a hidden iframe for PDF generation
      const pdfContent = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
          <title>${sanitizeText(report.venue_name)} - WVC Report</title>
          <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600&family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet">
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Inter', sans-serif; background: #fff; color: #292524; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1, h2, h3 { font-family: 'Playfair Display', serif; }
            h1 { font-size: 28px; margin-bottom: 8px; color: #292524; }
            h2 { font-size: 20px; margin: 24px 0 12px 0; color: #292524; border-bottom: 2px solid #D4AF37; padding-bottom: 8px; }
            h3 { font-size: 16px; margin: 16px 0 8px 0; color: #44403C; }
            p { margin-bottom: 8px; line-height: 1.6; }
            ul { padding-left: 20px; margin-bottom: 16px; }
            li { margin-bottom: 6px; line-height: 1.5; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid #E7E5E4; }
            .score-box { text-align: center; background: #FDFCF8; padding: 20px; border-radius: 12px; border: 2px solid #D4AF37; }
            .score-number { font-size: 48px; font-weight: 600; color: #292524; font-family: 'Playfair Display', serif; }
            .score-label { font-size: 12px; color: #78716C; text-transform: uppercase; letter-spacing: 1px; }
            .meta { color: #78716C; font-size: 14px; }
            .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; margin-right: 8px; margin-bottom: 8px; }
            .badge-positive { background: #ECFDF5; color: #065F46; }
            .badge-negative { background: #FEF2F2; color: #991B1B; }
            .section { margin-bottom: 24px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
            .highlight { color: #059669; }
            .concern { color: #DC2626; }
            .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #E7E5E4; text-align: center; color: #78716C; font-size: 12px; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1>${sanitizeText(report.venue_name)}</h1>
              <p class="meta">${sanitizeText(report.location)}</p>
              <p class="meta" style="margin-top: 8px;">
                <strong>Google Rating:</strong> ${report.google_rating}/5 (${report.total_reviews?.toLocaleString()} reviews) | 
                <strong>Confidence:</strong> ${report.confidence_level}
              </p>
            </div>
            <div class="score-box">
              <div class="score-number">${report.wvc_score?.total || 0}</div>
              <div class="score-label">WVC Score</div>
            </div>
          </div>
          
          <div class="section">
            <h2>Earned Badges</h2>
            ${report.badges?.map(b => `<span class="badge ${b.positive ? 'badge-positive' : 'badge-negative'}">${sanitizeText(b.name)}</span>`).join('') || 'No badges'}
          </div>

          <div class="grid">
            <div class="section">
              <h2>Best Highlights</h2>
              <ul>
                ${report.best_highlights?.map(h => `<li class="highlight">[+] ${sanitizeText(h)}</li>`).join('') || '<li>No highlights</li>'}
              </ul>
            </div>
            <div class="section">
              <h2>Biggest Concerns</h2>
              <ul>
                ${report.biggest_concerns?.map(c => `<li class="concern">[!] ${sanitizeText(c)}</li>`).join('') || '<li>No concerns</li>'}
              </ul>
            </div>
          </div>

          ${report.red_flag_alerts?.length > 0 ? `
          <div class="section" style="background: #FEF2F2; padding: 16px; border-radius: 8px;">
            <h3 style="color: #991B1B;">[RED FLAG]</h3>
            <ul>${report.red_flag_alerts.map(a => `<li style="color: #991B1B;">${sanitizeText(a)}</li>`).join('')}</ul>
          </div>
          ` : ''}

          <div class="section">
            <h2>WVC Verdict</h2>
            <div class="grid">
              <div>
                <h3>Best For</h3>
                <ul>${report.best_for?.map(b => `<li>[+] ${sanitizeText(b)}</li>`).join('') || '<li>N/A</li>'}</ul>
              </div>
              <div>
                <h3>Avoid If</h3>
                <ul>${report.avoid_if?.map(a => `<li>[-] ${sanitizeText(a)}</li>`).join('') || '<li>N/A</li>'}</ul>
              </div>
            </div>
          </div>

          <div class="section">
            <h2>Negotiation Tips</h2>
            <ul>${report.negotiation_tips?.map(t => `<li>[Rs] ${sanitizeText(t)}</li>`).join('') || '<li>No tips available</li>'}</ul>
          </div>

          <div class="section">
            <h2>Category Ratings</h2>
            <table style="width: 100%; border-collapse: collapse;">
              ${report.category_ratings?.map(r => `
                <tr style="border-bottom: 1px solid #E7E5E4;">
                  <td style="padding: 8px 0;">${sanitizeText(r.category)}</td>
                  <td style="padding: 8px 0; text-align: right; font-weight: 600;">${r.score}/10</td>
                </tr>
              `).join('') || ''}
            </table>
          </div>

          <div class="footer">
            <p>Generated by WVC Venue Intelligence - ${new Date().toLocaleDateString()}</p>
            <p style="margin-top: 4px;">${sanitizeText(report.confidence_note) || 'Verify all details directly with the venue.'}</p>
            <p style="margin-top: 12px; font-size: 10px; color: #A8A29E; line-height: 1.5;">
              <strong>Disclaimer:</strong> WVC is a trust-first platform. We do not promote venues for payment or provide biased recommendations. Our insights are based on publicly available information, real reviews, and online content.
            </p>
          </div>
        </body>
        </html>
      `;

      // Create blob and download with UTF-8 encoding
      const blob = new Blob([pdfContent], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      
      // Open in new window for printing/saving as PDF
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
        toast.success('PDF ready! Use "Save as PDF" in the print dialog.');
      } else {
        // Fallback: download as HTML
        const link = document.createElement('a');
        link.href = url;
        link.download = `${report.venue_name.replace(/[^a-z0-9]/gi, '_')}_WVC_Report.html`;
        link.click();
        toast.success('Report downloaded! Open in browser and print to PDF.');
      }
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to generate PDF.');
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: `WVC Report: ${report.venue_name}`,
      text: `Check out this venue report for ${report.venue_name}. WVC Score: ${report.wvc_score?.total}/100`,
      url: window.location.href
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${shareData.title}\n${shareData.text}\n${shareData.url}`);
        toast.success('Report link copied to clipboard!');
      }
    } catch (error) {
      console.log('Share failed');
    }
  };

  const handleVerifyDetails = () => {
    navigate('/community', { state: { venue_id: report.id, venue_name: report.venue_name } });
  };

  const handleAddVisitReport = () => {
    if (!isLoggedIn) {
      requireLogin('add a visit report', () => {
        navigate('/venue-visit-reports', { state: { venue_name: report.venue_name, place_id: report.place_id } });
      });
      return;
    }
    navigate('/venue-visit-reports', { state: { venue_name: report.venue_name, place_id: report.place_id } });
  };

  const handleRequestVerification = async () => {
    if (!isLoggedIn) {
      requireLogin('request verification', () => setShowVerificationModal(true));
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/verification/request?token=${token}&venue_name=${encodeURIComponent(report.venue_name)}&venue_location=${encodeURIComponent(report.location || '')}&place_id=${report.place_id || ''}&notes=${encodeURIComponent(verificationNotes)}`);
      toast.success('Verification request submitted! Our team will review it.');
      setShowVerificationModal(false);
      setVerificationNotes('');
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'positive': return 'text-green-600 bg-green-50';
      case 'negative': return 'text-red-600 bg-red-50';
      default: return 'text-amber-600 bg-amber-50';
    }
  };

  if (!report) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin" />
      </div>
    );
  }

  const getConfidenceColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'high': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-stone-100 text-stone-600 border-stone-200';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend?.toLowerCase()) {
      case 'improving': return <TrendingUp className="w-4 h-4 text-emerald-600" />;
      case 'declining': return <TrendingDown className="w-4 h-4 text-red-600" />;
      default: return <Minus className="w-4 h-4 text-stone-400" />;
    }
  };

  const getBadgeIcon = (iconName) => {
    const icons = { 'users': Users, 'utensils': Utensils, 'camera': Camera, 'check-circle': CheckCircle, 'alert-triangle': AlertTriangle, 'car': Car, 'clock': Clock, 'dollar-sign': IndianRupee, 'sparkles': Sparkles, 'info': Info };
    return icons[iconName] || Award;
  };

  const getNearbyIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'airport': return Plane;
      case 'railway station': return Train;
      case 'hotel': return Hotel;
      default: return MapPin;
    }
  };

  const getVideoTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'walkthrough': return 'bg-blue-100 text-blue-700';
      case 'wedding highlight': return 'bg-pink-100 text-pink-700';
      case 'drone tour': return 'bg-purple-100 text-purple-700';
      case 'vlog/review': return 'bg-amber-100 text-amber-700';
      default: return 'bg-stone-100 text-stone-600';
    }
  };

  const getRealityCheckIcon = (status) => {
    switch (status) {
      case 'match': return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'mixed': return <AlertCircle className="w-5 h-5 text-amber-500" />;
      case 'mismatch': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <Info className="w-5 h-5 text-stone-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] noise-bg">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" onClick={() => navigate('/')} className="flex items-center gap-2 text-stone-600 hover:text-stone-900" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
              New Search
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate('/compare')} className="flex items-center gap-2 text-stone-600 hover:text-stone-900" data-testid="compare-link">
                <GitCompare className="w-4 h-4" />
                Compare
              </Button>
              <Button variant="ghost" onClick={() => navigate('/community')} className="flex items-center gap-2 text-stone-600 hover:text-stone-900" data-testid="community-link">
                <MessageSquare className="w-4 h-4" />
                Community
              </Button>
              <Button variant="ghost" onClick={handleShare} className="text-stone-600 hover:text-stone-900" data-testid="share-btn">
                <Share2 className="w-4 h-4" />
              </Button>
              <Button variant="ghost" onClick={handleExportPDF} className="flex items-center gap-2 text-stone-600 hover:text-stone-900" data-testid="export-pdf-btn">
                <Download className="w-4 h-4" />
                PDF
              </Button>
              <Button 
                variant={isShortlisted ? "default" : "outline"} 
                onClick={handleShortlist}
                disabled={isShortlisted}
                className={`flex items-center gap-2 ${isShortlisted ? 'bg-green-600 hover:bg-green-600 text-white' : 'border-green-600 text-green-600 hover:bg-green-50'}`}
                data-testid="shortlist-btn"
              >
                <Heart className={`w-4 h-4 ${isShortlisted ? 'fill-white' : ''}`} />
                {isShortlisted ? 'Shortlisted' : 'Shortlist'}
              </Button>
              <Button 
                variant={isRejected ? "default" : "outline"} 
                onClick={handleReject}
                disabled={isRejected}
                className={`flex items-center gap-2 ${isRejected ? 'bg-red-600 hover:bg-red-600 text-white' : 'border-red-600 text-red-600 hover:bg-red-50'}`}
                data-testid="reject-btn"
              >
                <ThumbsDown className="w-4 h-4" />
                {isRejected ? 'Rejected' : 'Reject'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main ref={reportRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Venue Summary Card */}
        <section className="mb-8 animate-fade-in" data-testid="venue-summary-card">
          <div className="card-base p-8">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getConfidenceColor(report.confidence_level)}`}>
                    {report.confidence_level} Confidence
                  </span>
                  <div className="flex items-center gap-1">
                    {getTrendIcon(report.recent_trend)}
                    <span className="text-sm text-stone-500">{report.recent_trend}</span>
                  </div>
                </div>

                <h1 className="font-serif text-3xl sm:text-4xl font-medium text-stone-900 mb-2" data-testid="venue-name">
                  {report.venue_name}
                </h1>
                
                <div className="flex items-center gap-4 text-stone-500 mb-6">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{report.location}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
                    <span className="text-sm font-medium text-stone-900">{report.google_rating}</span>
                    <span className="text-sm">({report.total_reviews?.toLocaleString()} reviews)</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="data-label mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      Best Highlights
                    </h3>
                    <ul className="space-y-2">
                      {report.best_highlights?.map((highlight, idx) => (
                        <li key={idx} className="text-sm text-stone-600 flex items-start gap-2">
                          <span className="text-emerald-500 mt-1">•</span>
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="data-label mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      Biggest Concerns
                    </h3>
                    <ul className="space-y-2">
                      {report.biggest_concerns?.map((concern, idx) => (
                        <li key={idx} className="text-sm text-stone-600 flex items-start gap-2">
                          <span className="text-amber-500 mt-1">•</span>
                          {concern}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {report.red_flag_alerts?.length > 0 && (
                  <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <h3 className="text-sm font-medium text-red-800 flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4" />
                      Red Flag Alert
                    </h3>
                    <ul className="space-y-1">
                      {report.red_flag_alerts.map((alert, idx) => (
                        <li key={idx} className="text-sm text-red-700">{alert}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Right side - WVC Score */}
              <div className="flex flex-col items-center lg:items-end shrink-0">
                <ScoreCircle score={report.wvc_score?.total || 0} />
                <div className="mt-4 grid grid-cols-2 gap-3 text-center">
                  <div>
                    <p className="text-xs text-stone-400 uppercase tracking-wider">Trust</p>
                    <p className="font-serif text-xl font-medium text-stone-900">{report.wvc_score?.trust_score}/30</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-400 uppercase tracking-wider">Hospitality</p>
                    <p className="font-serif text-xl font-medium text-stone-900">{report.wvc_score?.hospitality_score}/25</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-400 uppercase tracking-wider">Experience</p>
                    <p className="font-serif text-xl font-medium text-stone-900">{report.wvc_score?.venue_experience_score}/25</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-400 uppercase tracking-wider">Value</p>
                    <p className="font-serif text-xl font-medium text-stone-900">{report.wvc_score?.value_score}/20</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-stone-100">
              <h3 className="data-label mb-3">Earned Badges</h3>
              <div className="flex flex-wrap gap-2">
                {report.badges?.map((badge, idx) => {
                  const IconComponent = getBadgeIcon(badge.icon);
                  return (
                    <span key={idx} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${badge.positive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid={`badge-${idx}`}>
                      <IconComponent className="w-3.5 h-3.5" />
                      {badge.name}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Score Explanation Section */}
        {report.score_explanation && (
          <section className="mb-8 animate-fade-in">
            <div className="card-base p-6">
              <button
                onClick={() => setShowScoreExplanation(!showScoreExplanation)}
                className="w-full flex items-center justify-between text-left"
                data-testid="score-explanation-toggle"
              >
                <div className="flex items-center gap-3">
                  <Shield className="w-6 h-6 text-[#D4AF37]" />
                  <div>
                    <h3 className="font-medium text-stone-900">How is this score calculated?</h3>
                    <p className="text-sm text-stone-500">Understand the factors behind the WVC Score</p>
                  </div>
                </div>
                {showScoreExplanation ? (
                  <ChevronUp className="w-5 h-5 text-stone-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-stone-400" />
                )}
              </button>
              
              {showScoreExplanation && (
                <div className="mt-6 pt-6 border-t border-stone-200 space-y-4">
                  {/* Review Metadata */}
                  {report.review_metadata && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="p-3 bg-stone-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-stone-900">{report.review_metadata.reviews_analyzed}</p>
                        <p className="text-xs text-stone-500">Reviews Analyzed</p>
                      </div>
                      <div className="p-3 bg-stone-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-stone-900">{report.review_metadata.total_available}</p>
                        <p className="text-xs text-stone-500">Total Available</p>
                      </div>
                      <div className="p-3 bg-stone-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-amber-600">{report.review_metadata.recent_reviews_count}</p>
                        <p className="text-xs text-stone-500">Recent (60 days)</p>
                      </div>
                      <div className="p-3 bg-stone-50 rounded-lg text-center">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          report.review_metadata.data_freshness === 'fresh' ? 'bg-green-100 text-green-700' :
                          report.review_metadata.data_freshness === 'moderate' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {report.review_metadata.data_freshness?.replace('_', ' ').toUpperCase()}
                        </span>
                        <p className="text-xs text-stone-500 mt-1">Data Freshness</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Score Factors */}
                  <div className="space-y-3">
                    {report.score_explanation.map((factor, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-stone-900">{factor.factor}</span>
                            <span className="text-xs text-stone-400">({factor.weight})</span>
                          </div>
                          <p className="text-xs text-stone-500 mt-0.5">{factor.description}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-stone-700">{factor.current_value}</span>
                          <span className={`w-2 h-2 rounded-full ${
                            factor.impact === 'positive' ? 'bg-green-500' :
                            factor.impact === 'negative' ? 'bg-red-500' :
                            'bg-amber-500'
                          }`} />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <p className="text-xs text-stone-400 mt-4">
                    WVC scores are calculated using publicly available data. We do not use paid placements or hidden boosts.
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* CTAs Section - Ask Community + Add Visit Report */}
        <section className="mb-8 animate-fade-in">
          <div className="card-base p-6 bg-gradient-to-r from-stone-900 to-stone-800 text-white">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <HelpCircle className="w-8 h-8 text-[#D4AF37]" />
                <div>
                  <h3 className="font-medium">Share your insights or ask the community</h3>
                  <p className="text-sm text-stone-300">Help others make informed decisions</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={handleVerifyDetails} className="bg-[#D4AF37] hover:bg-[#B8962F] text-stone-900 font-medium px-6" data-testid="verify-details-btn">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Ask Community
                </Button>
                <Button onClick={handleAddVisitReport} variant="outline" className="border-white text-white hover:bg-white/10 font-medium px-6" data-testid="add-visit-report-btn">
                  <PenSquare className="w-4 h-4 mr-2" />
                  Add Visit Report
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Venue Intelligence Disclaimer */}
        <section className="mb-8 animate-fade-in">
          <div className="flex items-start gap-3 p-4 bg-stone-50 border border-stone-200 rounded-xl">
            <Info className="w-5 h-5 text-stone-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-stone-500 leading-relaxed">
              This report is created to help you better understand a venue before reaching out. While we strive to provide accurate and unbiased insights, we strongly recommend contacting the venue directly to validate availability, pricing, services, and latest details before making decisions.
            </p>
          </div>
        </section>

        {/* Request Verification CTA */}
        <section className="mb-8 animate-fade-in">
          <div className="card-base p-6 border-2 border-dashed border-amber-300 bg-amber-50/50">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <ClipboardCheck className="w-8 h-8 text-amber-600" />
                <div>
                  <h3 className="font-medium text-stone-900">Want verified details from our team?</h3>
                  <p className="text-sm text-stone-500">Request a professional venue verification report</p>
                </div>
              </div>
              <Button 
                onClick={() => isLoggedIn ? setShowVerificationModal(true) : requireLogin('request verification', () => setShowVerificationModal(true))}
                className="bg-amber-600 hover:bg-amber-700 text-white font-medium px-6" 
                data-testid="request-verification-btn"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                Request Venue Verification
              </Button>
            </div>
          </div>
        </section>

        {/* Bento Grid Layout */}
        <div className="bento-grid">
          {/* Website Insights - Spans 6 columns */}
          <section className="col-span-12 lg:col-span-6 card-base animate-fade-in delay-100" data-testid="website-insights">
            <h2 className="section-title mb-6 flex items-center gap-2">
              <Globe className="w-6 h-6 text-[#D4AF37]" />
              Venue Website Insights
            </h2>
            
            {report.website_insights?.data_available ? (
              <div className="space-y-4">
                {report.website_insights.website_url && (
                  <a href={report.website_insights.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline">
                    <ExternalLink className="w-4 h-4" />
                    Visit Official Website
                  </a>
                )}

                {/* Venue Types */}
                {report.website_insights.venue_types?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Venue Type</h4>
                    <div className="flex flex-wrap gap-2">
                      {report.website_insights.venue_types.map((vtype, idx) => (
                        <span key={idx} className="px-2 py-1 bg-amber-100 rounded text-xs text-amber-700 font-medium">{vtype}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Capacity & Rooms in a grid */}
                <div className="grid grid-cols-2 gap-4">
                  {report.website_insights.capacity_details && (
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Capacity</h4>
                      <p className="text-sm font-medium text-stone-800">{report.website_insights.capacity_details}</p>
                    </div>
                  )}
                  
                  {report.website_insights.rooms_details && (
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Rooms</h4>
                      <p className="text-sm font-medium text-stone-800">{report.website_insights.rooms_details}</p>
                    </div>
                  )}
                </div>
                
                {/* Amenities */}
                {report.website_insights.amenities?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Amenities & Facilities</h4>
                    <div className="flex flex-wrap gap-2">
                      {report.website_insights.amenities.map((amenity, idx) => (
                        <span key={idx} className="px-2 py-1 bg-stone-100 rounded text-xs text-stone-600">{amenity}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Food Policy */}
                {report.website_insights.food_policy && (
                  <div>
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Food & Catering</h4>
                    <p className="text-sm text-stone-600">{report.website_insights.food_policy}</p>
                  </div>
                )}
                
                {/* Policies */}
                {report.website_insights.policies?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Policies Mentioned</h4>
                    <div className="flex flex-wrap gap-2">
                      {report.website_insights.policies.slice(0, 6).map((policy, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-50 rounded text-xs text-blue-700">{policy}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Assurances from venue */}
                {report.website_insights.assurances?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Venue Assurances</h4>
                    <div className="flex flex-wrap gap-2">
                      {report.website_insights.assurances.map((assurance, idx) => (
                        <span key={idx} className="px-2 py-1 bg-emerald-50 rounded text-xs text-emerald-700 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {assurance}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-stone-400 italic mt-4 pt-4 border-t border-stone-100">Source: Official Website</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <Globe className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-500">Official website data not found / not accessible</p>
                {report.website_insights?.website_url && (
                  <a href={report.website_insights.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline mt-2">
                    <ExternalLink className="w-4 h-4" />
                    Try visiting directly
                  </a>
                )}
              </div>
            )}
          </section>

          {/* Website vs Reviews Reality Check - Spans 6 columns */}
          <section className="col-span-12 lg:col-span-6 card-base animate-fade-in delay-150" data-testid="reality-check">
            <h2 className="section-title mb-6 flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#D4AF37]" />
              Website vs Reviews Reality Check
            </h2>
            
            {report.website_vs_reviews?.website_claims?.length > 0 ? (
              <div className="space-y-6">
                <div>
                  <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Claims vs Reality</h4>
                  <div className="space-y-3">
                    {report.website_vs_reviews.reality_checks?.map((check, idx) => (
                      <div key={idx} className="p-3 bg-stone-50 rounded-lg border border-stone-100">
                        <div className="flex items-start gap-3">
                          {getRealityCheckIcon(check.status)}
                          <div className="flex-1">
                            <p className="text-sm font-medium text-stone-900">{check.claim}</p>
                            <p className="text-xs text-stone-500 mt-1">{check.explanation}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {report.website_vs_reviews.reality_gaps?.length > 0 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <h4 className="text-sm font-medium text-red-800 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Reality Gaps (Red Flags)
                    </h4>
                    <ul className="space-y-1">
                      {report.website_vs_reviews.reality_gaps.map((gap, idx) => (
                        <li key={idx} className="text-sm text-red-700">{gap}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <p className="text-xs text-stone-400 italic">
                  Confidence: {report.website_vs_reviews.confidence}
                </p>
              </div>
            ) : (
              <div className="text-center py-8">
                <Shield className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-500">Not enough website claims available for comparison</p>
              </div>
            )}
          </section>

          {/* Reviews Intelligence - Spans 8 columns */}
          <section className="col-span-12 lg:col-span-8 card-base animate-fade-in delay-200" data-testid="reviews-intelligence">
            <h2 className="section-title mb-6 flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#D4AF37]" />
              Reviews Intelligence
            </h2>

            <Tabs defaultValue="good" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-stone-100 rounded-full p-1">
                <TabsTrigger value="good" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="good-reviews-tab">Good Reviews</TabsTrigger>
                <TabsTrigger value="bad" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="bad-reviews-tab">Bad Reviews</TabsTrigger>
              </TabsList>
              
              <TabsContent value="good" className="mt-0">
                <ul className="space-y-3">
                  {report.good_review_summary?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </TabsContent>
              
              <TabsContent value="bad" className="mt-0">
                <ul className="space-y-3">
                  {report.bad_review_summary?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-stone-600">
                      <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </TabsContent>
            </Tabs>

            <div className="mt-8 pt-6 border-t border-stone-100">
              <h3 className="data-label mb-4">Review Themes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-emerald-600 font-medium mb-3">POSITIVE THEMES</p>
                  {report.positive_themes?.slice(0, 5).map((theme, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2">
                      <span className="text-sm text-stone-600">{theme.theme}</span>
                      <span className="text-xs text-stone-400">{theme.count} mentions</span>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs text-red-600 font-medium mb-3">NEGATIVE THEMES</p>
                  {report.negative_themes?.slice(0, 5).map((theme, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2">
                      <span className="text-sm text-stone-600">{theme.theme}</span>
                      <span className="text-xs text-stone-400">{theme.count} mentions</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Category Ratings - Spans 4 columns */}
          <section className="col-span-12 lg:col-span-4 card-base animate-fade-in delay-250" data-testid="category-ratings">
            <h2 className="section-title mb-6">Category Ratings</h2>
            <div className="space-y-4">
              {report.category_ratings?.map((rating, idx) => (
                <CategoryRatingBar key={idx} rating={rating} />
              ))}
            </div>
          </section>

          {/* Transport Locations - Spans 6 columns */}
          {report.transport_locations?.length > 0 && (
            <section className="col-span-12 lg:col-span-6 card-base animate-fade-in delay-300" data-testid="transport-locations">
              <h2 className="section-title mb-6 flex items-center gap-2">
                <Plane className="w-6 h-6 text-[#D4AF37]" />
                Distance from Key Landmarks
              </h2>
              <div className="space-y-3">
                {report.transport_locations.map((loc, idx) => {
                  const IconComponent = getNearbyIcon(loc.type);
                  return (
                    <div key={idx} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl border border-stone-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-stone-200">
                          <IconComponent className="w-5 h-5 text-stone-600" />
                        </div>
                        <div>
                          <p className="font-medium text-stone-900 text-sm">{loc.name}</p>
                          <p className="text-xs text-stone-400">{loc.type}</p>
                        </div>
                      </div>
                      <p className="font-serif text-lg font-medium text-stone-900">{loc.distance}</p>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Nearby Hotels - Spans 6 columns */}
          {report.nearby_hotels?.length > 0 && (
            <section className="col-span-12 lg:col-span-6 card-base animate-fade-in delay-350" data-testid="nearby-hotels">
              <h2 className="section-title mb-6 flex items-center gap-2">
                <Hotel className="w-6 h-6 text-[#D4AF37]" />
                Nearby Hotels (within 10km)
              </h2>
              <div className="space-y-3">
                {report.nearby_hotels.map((hotel, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl border border-stone-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-stone-200">
                        <Hotel className="w-5 h-5 text-stone-600" />
                      </div>
                      <div>
                        <p className="font-medium text-stone-900 text-sm">{hotel.name}</p>
                        <p className="text-xs text-stone-400">Guest Accommodation</p>
                      </div>
                    </div>
                    <p className="font-serif text-lg font-medium text-stone-900">{hotel.distance}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Highlighted Staff */}
          <section className="col-span-12 lg:col-span-6 card-base animate-fade-in delay-400" data-testid="highlighted-staff">
            <h2 className="section-title mb-6 flex items-center gap-2">
              <Users className="w-6 h-6 text-[#D4AF37]" />
              Highlighted Staff
            </h2>
            <div className="space-y-4">
              {report.highlighted_staff?.length > 0 ? report.highlighted_staff.map((staff, idx) => (
                <div key={idx} className="p-4 bg-stone-50 rounded-xl border border-stone-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-stone-200 rounded-full flex items-center justify-center">
                        <span className="font-medium text-stone-600">{staff.name?.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{staff.name}</p>
                        <p className="text-xs text-stone-400">{staff.role}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${staff.sentiment === 'positive' ? 'bg-emerald-100 text-emerald-700' : staff.sentiment === 'negative' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {staff.mentions} mentions
                    </span>
                  </div>
                  <p className="text-sm text-stone-500">{staff.context}</p>
                </div>
              )) : (
                <p className="text-sm text-stone-400 text-center py-4">No specific staff mentions found</p>
              )}
            </div>
          </section>

          {/* Management Responsiveness */}
          <section className="col-span-12 lg:col-span-6 card-base animate-fade-in delay-450" data-testid="management-responsiveness">
            <h2 className="section-title mb-6">Management Responsiveness</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                <span className="text-sm text-stone-600">Responds to reviews?</span>
                <span className="text-sm font-medium text-stone-900">{report.management_responsiveness?.responds_to_reviews}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                <span className="text-sm text-stone-600">Response tone</span>
                <span className="text-sm font-medium text-stone-900">{report.management_responsiveness?.tone}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                <span className="text-sm text-stone-600">Response rate</span>
                <span className="text-sm font-medium text-stone-900">{report.management_responsiveness?.response_rate}</span>
              </div>
            </div>
          </section>

          {/* YouTube Videos */}
          <section className="col-span-12 card-base animate-fade-in delay-500" data-testid="youtube-videos">
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title flex items-center gap-2">
                <Youtube className="w-6 h-6 text-red-600" />
                YouTube Intelligence
              </h2>
              <div className="flex items-center gap-2 text-sm text-stone-500">
                <Info className="w-4 h-4" />
                {report.youtube_videos?.length || 0} videos found
              </div>
            </div>

            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <h3 className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Top 3 Videos to Watch First
              </h3>
              <ul className="space-y-1">
                {report.top_videos?.map((tip, idx) => (
                  <li key={idx} className="text-sm text-amber-700 flex items-start gap-2">
                    <span>{idx + 1}.</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {report.youtube_videos?.slice(0, 8).map((video, idx) => (
                <a key={idx} href={video.url} target="_blank" rel="noopener noreferrer" className="group block bg-stone-50 rounded-xl overflow-hidden border border-stone-100 hover:border-stone-300 transition-all hover:shadow-md" data-testid={`youtube-video-${idx}`}>
                  <div className="relative aspect-video bg-stone-200">
                    {video.thumbnail ? (
                      <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Youtube className="w-8 h-8 text-stone-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <Play className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <span className={`absolute bottom-2 left-2 px-2 py-0.5 rounded text-xs font-medium ${getVideoTypeColor(video.video_type)}`}>
                      {video.video_type}
                    </span>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium text-stone-900 line-clamp-2 group-hover:text-stone-600 transition-colors">{video.title}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-stone-400">{video.channel}</p>
                      {video.date && <p className="text-xs text-stone-400">{video.date}</p>}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </section>

          {/* Venue Facts */}
          <section className="col-span-12 lg:col-span-4 card-base animate-fade-in delay-100" data-testid="venue-facts">
            <h2 className="section-title mb-6">Venue Facts</h2>
            <div className="space-y-3">
              {report.venue_facts?.map((fact, idx) => (
                <div key={idx} className="flex items-start justify-between gap-4 p-3 bg-stone-50 rounded-lg">
                  <span className="text-sm text-stone-500 flex-shrink-0 min-w-[100px]">{fact.label}</span>
                  <div className="flex items-center gap-2 text-right">
                    <span className="text-sm font-medium text-stone-900">{fact.value}</span>
                    {!fact.verified && <span className="text-xs text-amber-600 whitespace-nowrap">(Unverified)</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* WVC Verdict */}
          <section className="col-span-12 lg:col-span-8 card-base bg-stone-900 text-white animate-fade-in delay-200" data-testid="wvc-verdict">
            <h2 className="font-serif text-2xl font-medium mb-6 flex items-center gap-2">
              <Award className="w-6 h-6 text-[#D4AF37]" />
              WVC Verdict
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xs font-semibold tracking-wider uppercase text-stone-400 mb-3">Best For</h3>
                <ul className="space-y-2">
                  {report.best_for?.map((item, idx) => (
                    <li key={idx} className="text-sm text-stone-300 flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xs font-semibold tracking-wider uppercase text-stone-400 mb-3">Avoid If</h3>
                <ul className="space-y-2">
                  {report.avoid_if?.map((item, idx) => (
                    <li key={idx} className="text-sm text-stone-300 flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-stone-700">
              <h3 className="text-xs font-semibold tracking-wider uppercase text-stone-400 mb-3">Negotiation Tips</h3>
              <ul className="space-y-2">
                {report.negotiation_tips?.map((tip, idx) => (
                  <li key={idx} className="text-sm text-stone-300 flex items-start gap-2">
                    <IndianRupee className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 p-4 bg-stone-800 rounded-xl">
              <p className="text-xs text-stone-400 flex items-start gap-2">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {report.confidence_note}
              </p>
            </div>
          </section>
        </div>

        {/* Action Buttons */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-4 animate-fade-in">
          <Button onClick={() => navigate('/compare')} className="btn-secondary flex items-center gap-2" data-testid="compare-venues-btn">
            <GitCompare className="w-4 h-4" />
            Compare Venues
          </Button>
          <Button onClick={handleVerifyDetails} className="btn-secondary flex items-center gap-2" data-testid="ask-community-btn">
            <MessageSquare className="w-4 h-4" />
            Ask Community
          </Button>
          <Button onClick={() => navigate('/')} className="btn-primary" data-testid="search-again-btn">
            Search Another Venue
          </Button>
        </div>
      </main>

      {/* Helpful Feedback - Center Aligned */}
      <div className="max-w-2xl mx-auto px-4 mt-8">
        <HelpfulFeedback reportId={report?.id} venueName={report?.venue_name} />
      </div>

      <footer className="border-t border-stone-200 py-8 px-4 mt-16">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-stone-900 rounded-full flex items-center justify-center">
              <span className="text-white font-serif text-xs font-bold">W</span>
            </div>
            <span className="font-serif text-sm text-stone-600">WVC Venue Intelligence</span>
          </div>
          <p className="text-xs text-stone-400 text-center">Report generated with AI analysis. Verify details with venue directly.</p>
          <p className="text-xs text-stone-400 text-center max-w-2xl leading-relaxed">
            <span className="font-medium">Disclaimer:</span> WVC is a trust-first platform. We do not promote venues for payment or provide biased recommendations. Our insights are based on publicly available information, real reviews, and online content.
          </p>
        </div>
      </footer>

      {/* Verification Request Modal */}
      <Dialog open={showVerificationModal} onOpenChange={setShowVerificationModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Request Venue Verification</DialogTitle>
            <DialogDescription>
              Our team will visit and verify details for <strong>{report?.venue_name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <p>A WVC team member will contact you to coordinate the verification. This typically includes:</p>
              <ul className="list-disc ml-4 mt-2 space-y-1">
                <li>On-ground venue inspection</li>
                <li>Capacity and amenities verification</li>
                <li>Staff and service assessment</li>
                <li>Photo documentation</li>
              </ul>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Any specific details you want verified? (optional)</label>
              <Textarea
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
                placeholder="e.g., Room quality, catering options, parking capacity..."
                rows={3}
              />
            </div>
            <Button 
              onClick={handleRequestVerification}
              className="w-full bg-amber-600 hover:bg-amber-700"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Verification Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VenueReportPage;
